// Angular
import { Component, Inject, OnInit, AfterViewInit, QueryList, ViewChild, ViewChildren, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';

// Libraries
import { startWith, takeUntil } from 'rxjs';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

// Models
import { UserChannelViewModel } from '../../../../../channel/models/user-channel-view.model';
import { UserUnitPeriodChannelDto } from '../../../../../channel/models/user-unit-period-channel-dto';
import { AppCleanup, Colors, LineStyleOption, LineStyles, LineWeights } from '../../../../../../shared/models';

// Services
import { ChannelService } from '../../../../../channel/services';
import { GlobalService } from '../../../../../../shared/services';
import { UtilityService } from '../../../../../../shared/services/utility.service';

// Helpers
import { UtilityHelper } from '../../../../../../shared/helpers';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { LogicChannelDataPoint } from '../../../../models/job-data.model';

@Component({
  selector: 'edit-user-channels-dialog',
  templateUrl: './edit-user-channels-dialog.component.html',
  styleUrls: ['./edit-user-channels-dialog.component.scss'],
})
export class EditUserChannelsDialogComponent extends AppCleanup implements OnInit, AfterViewInit, OnDestroy {
  componentTitle: string = 'Edit user settings for job';
  /**Copy and mapped User Job Channels from Job Service */
  userJobChannels: UserUnitPeriodChannelDto[] = [];
  logicChannels: LogicChannelDataPoint[] = [];
  /**Columns to display in Mat-Table */
  displayedColumns = [
    'drag',
    'unitPeriodChannel.channel.label',
    'unitPeriodChannel.unit.name',
    'unitPeriodChannel.unit.description',
    'min',
    'max',
    'colorHex',
    'lineStyle',
    'lineWeight',
    'showGraph',
    'display',
  ];
  /**Datasource for Mat-Table that stores the User Job Channel's and assigns as an Input to <mat-table> element  */
  dataSource: UserUnitPeriodChannelDto[];
  /**Array of HexColors that will be used for Channel color-assignment */
  presetColors: Array<string> = [...Colors.ChartDefaults];
  /**Array of Line Styles that will be used for Channel line-style assignment */
  lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];
  /**Array of Line Weight values that will be used for Channel line-weight assignment */
  lineWeightOptions: Array<number> = [...LineWeights.List];
  dragDisabled: boolean = true;
  showGraphCount: number;
  showGraphCountMax: number = 10;
  disabledGraphArray: boolean[] = []; // Array contains boolean variables that show if graph toggle is enabled or not
  @ViewChildren('graphToggle') graphSlideToggles: QueryList<MatSlideToggle>;

  showDisplayCount: number;
  showDisplayCountMax: number = 12;
  disabledDisplayArray: boolean[] = []; // Array contains boolean variables that show if display toggle is enabled or not
  @ViewChildren('displayToggle') displaySlideToggles: QueryList<MatSlideToggle>;

  dataLoaded: boolean = false;
  userChannelList: UserChannelViewModel[] = [];
  @ViewChild('table') table: MatTable<any>;
  /**data parameter is passed from Parent component when Dialog is instantiated. Used here to get the job key from parent. */
  isWidget: boolean = false;
  jobChartWidget: boolean = false;
  isMobile: boolean = false;
  messages = {
    displayCountWarning: 'Display will show max of 12 channels',
    graphCountWarning: 'Graph will show max of 10 channels',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      key: string;
      userUnitPeriodChannels: UserUnitPeriodChannelDto[];
      isWidget: boolean;
      jobChartWidget: boolean;
    },
    private channelService: ChannelService,
    private globalService: GlobalService,
    private utilityService: UtilityService,
    public dialogRef: MatDialogRef<EditUserChannelsDialogComponent>
  ) {
    super();
  }

  ngOnInit() {
    this.dataLoaded = false;

    this.isWidget = this.data?.isWidget;
    this.jobChartWidget = this.data?.jobChartWidget;

    if (this.isWidget) {
      if (this.jobChartWidget) {
        this.displayedColumns = [
          'unitPeriodChannel.channel.label',
          'unitPeriodChannel.unit.name',
          'unitPeriodChannel.unit.description',
          'showGraph',
        ];
      } else {
        this.displayedColumns = [
          'unitPeriodChannel.channel.label',
          'unitPeriodChannel.unit.name',
          'unitPeriodChannel.unit.description',
          'display',
        ];
      }
    }

    // Determines if mobile device being used.
    this.globalService.isMobile.pipe(takeUntil(this.destroy)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.channelService.userChannels$.pipe(takeUntil(this.destroy)).subscribe((userChannels) => {
      if (userChannels) {
        this.userChannelList = userChannels;
        this.userJobChannels = this.data.userUnitPeriodChannels;
        this.userChannelList.forEach((uc) => this.disabledGraphArray.push(false));

        // Update the user job channel settings with the user channel settings. This is also
        // done on the API side, but is done here as well to make sure the settings are reflected
        // when changes are made to the user channel settings while the job is already opened.
        // But why, Jacob?
        this.updateUserUnitPeriodsWithUserChannels();

        this.userJobChannels.map((ujc) => {
          ujc.lineStyleOption = this.lineStyleOptions.find((lso) => lso.lineStyle == ujc.lineStyle);
        });

        this.dataSource = UtilityHelper.deepCopy(this.userJobChannels);
        this.dataLoaded = true;
        this.checkTotalGraphCount();
        this.checkTotalDisplayCount();
        this.utilityService.initializeChartScrolling();
      }
    });
  }

  ngAfterViewInit() {
    // TODO This is here because ViewChildren variable is not ready at ngAfterViewInit yet. Needs to be a better way
    setTimeout(() => {
      this.checkGraphDisableSliders();
    }, 100);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  get shouldShowDisplayWarning(): boolean {
    if (this.isWidget && this.jobChartWidget) {
      return false;
    }

    return this.showDisplayCount >= this.showDisplayCountMax;
  }

  get shouldShowGraphWarning(): boolean {
    if (this.isWidget && !this.jobChartWidget) {
      return false;
    }

    return this.showGraphCount >= this.showGraphCountMax;
  }

  updateUserUnitPeriodsWithUserChannels() {
    this.userJobChannels.forEach((userUnitPeriodChannel) => {
      let userChannel = this.userChannelList.find(
        (uc) => uc.channelId === userUnitPeriodChannel.unitPeriodChannel.channelId
      );
      if (userChannel) {
        userUnitPeriodChannel.colorHex = userChannel.colorHex;
        userUnitPeriodChannel.lineStyle = userChannel.lineStyle;
        userUnitPeriodChannel.lineWeight = userChannel.lineWeight;
      }
    });
  }

  /**
   * Changes the line style for the User Job Channel
   * @param userJobChannel User Job Channel to change Line Style for
   * @param lineStyleOption Line style to change User Job Channel to
   */
  changeLineStyle(userJobChannel: UserUnitPeriodChannelDto, lineStyleOption: LineStyleOption) {
    userJobChannel.lineStyle = lineStyleOption.lineStyle;
    userJobChannel.lineStyleOption = lineStyleOption;
  }

  /**
   * Changes the line weight (thickness) for the User Job Channel
   * @param userJobChannel User Job Channel to change Line Weight for
   * @param lineWeight Line weight (thickness) to change User Job Channel to
   */
  changeLineWeight(userJobChannel: UserUnitPeriodChannelDto, lineWeight: number) {
    userJobChannel.lineWeight = lineWeight;
  }

  drop(event) {
    this.dragDisabled = true;

    if (event.previousContainer === event.container) {
      moveItemInArray(this.dataSource, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    this.table.renderRows();
  }

  userSettingExists(channelId: number) {
    if (!this.userChannelList || this.userChannelList.length == 0) {
      return false;
    }

    return this.userChannelList.find((userChannel) => userChannel.channelId === channelId);
  }

  graphToggleAll(value: boolean) {
    this.dataSource.forEach((userUnitPeriodChannel) => {
      userUnitPeriodChannel.showGraph = value;
    });

    this.checkTotalGraphCount();
  }

  checkTotalGraphCount(userUnitPeriodChannel: UserUnitPeriodChannelDto = null) {
    setTimeout(() => {
      this.checkGraphDisableSliders();
    }, 100);

    if (userUnitPeriodChannel) {
      userUnitPeriodChannel.showGraph = !userUnitPeriodChannel.showGraph;
      let findIndex = this.userJobChannels.findIndex((ujc) => ujc.id === userUnitPeriodChannel.id);
      if (findIndex >= 0) {
        this.userJobChannels[findIndex].showGraph = userUnitPeriodChannel.showGraph;
      }
    }

    this.showGraphCount = this.dataSource.filter((ch) => ch.showGraph).length;
  }

  checkGraphDisableSliders() {
    let sumOfActive = 0;
    let disableIndexArray = [];
    if (this.graphSlideToggles && this.graphSlideToggles.changes) {
      this.graphSlideToggles.changes.pipe(startWith([undefined])).subscribe(() => {
        let slideToggleArray = this.graphSlideToggles.toArray();
        for (let i = 0; i < slideToggleArray.length; i++) {
          this.disabledGraphArray[i] = false;
          if (slideToggleArray[i].checked) {
            sumOfActive++;
          } else {
            disableIndexArray.push(i);
          }

          if (sumOfActive > this.showGraphCountMax) {
            slideToggleArray[i].checked = false;
            this.disabledGraphArray[i] = true;
          }
        }

        if (sumOfActive >= this.showGraphCountMax) {
          disableIndexArray.forEach((index) => {
            this.disabledGraphArray[index] = true;
          });
        }
      });
    }
  }

  displayToggleAll(value: boolean) {
    this.dataSource.forEach((userUnitPeriodChannel) => {
      userUnitPeriodChannel.display = value;
    });

    this.checkTotalDisplayCount();
  }

  checkTotalDisplayCount(userUnitPeriodChannel: UserUnitPeriodChannelDto = null) {
    setTimeout(() => {
      this.checkDisplayDisableSliders();
    }, 100);

    if (userUnitPeriodChannel) {
      userUnitPeriodChannel.display = !userUnitPeriodChannel.display;
      let findIndex = this.userJobChannels.findIndex((ujc) => ujc.id === userUnitPeriodChannel.id);
      if (findIndex >= 0) {
        this.userJobChannels[findIndex].display = userUnitPeriodChannel.display;
      }
    }

    this.showDisplayCount = this.dataSource.filter((ch) => ch.display).length;
  }

  checkDisplayDisableSliders() {
    let sumOfActive = 0;
    let disableIndexArray = [];
    if (this.displaySlideToggles && this.displaySlideToggles.changes) {
      this.displaySlideToggles.changes.pipe(startWith([undefined])).subscribe(() => {
        let slideToggleArray = this.displaySlideToggles.toArray();
        for (let i = 0; i < slideToggleArray.length; i++) {
          this.disabledDisplayArray[i] = false;
          if (slideToggleArray[i].checked) {
            sumOfActive++;
          } else {
            disableIndexArray.push(i);
          }

          if (sumOfActive > this.showDisplayCountMax) {
            slideToggleArray[i].checked = false;
            this.disabledDisplayArray[i] = true;
          }
        }

        if (sumOfActive >= this.showDisplayCountMax) {
          disableIndexArray.forEach((index) => {
            this.disabledDisplayArray[index] = true;
          });
        }
      });
    }
  }

  submit() {
    this.userJobChannels = this.dataSource;
    let displayCount = 0;
    let graphCount = 0;
    this.userJobChannels.forEach((ujc) => {
      if (graphCount < this.showGraphCountMax) {
        if (ujc.showGraph) {
          graphCount++;
        }
      } else {
        ujc.showGraph = false;
      }

      if (displayCount < this.showDisplayCountMax) {
        if (ujc.display) {
          displayCount++;
        }
      } else {
        ujc.display = false;
      }
    });

    this.dialogRef.close({
      key: this.data.key,
      userJobChannels: this.userJobChannels,
    });
  }
}
